// Stadium Trial — companion (pet) system for the 2v2 test.
//
// TEST FEATURE (PVE_PETS_ENABLED): the player's equipped familiar fights
// alongside them, and EVERY enemy brings its own companion (a true 2v2).
// Interlaced turn order: player → enemy → ally pet → enemy pet.
//
// Stats architecture: a pet has NO stat sheet of its own — everything derives
// from its MASTER's live stats, so future pet levels only need to grow the
// `petScaling` multiplier (nothing else to migrate):
//   - Max HP  = 25% of the master's max HP + 10% of the master's defense.
//   - Offense = % of a master base hit (computeHit with the master's stats).
//   - Defense = the master's defense reduction (shared formula).
//
// Ally pet kit: 4 energy per pet turn, 3 spells (Battle Cry / Zone Heal /
// Pet Strike — see PET_SPELLS). Enemy pet kit: one scripted AoE strike at
// 30% of its master's damage, hitting BOTH the player and the ally pet.
//
// A dead master does NOT end the fight: the surviving pet can finish the game
// (both sides). A side loses only when master AND pet are down.

import type { EquipmentStats } from "../../league-duel/engine/combat";

/** Test flag — flips the interactive (variant B) fight to 2v2 with pets. */
export const PVE_PETS_ENABLED = true;

// ── Derivation rules (all master-relative) ────────────────────────────────────
export const PET_HP_PERCENT_MASTER_HP = 25;    // 25% of the master's max HP…
export const PET_HP_PERCENT_MASTER_DEF = 10;   // …+ 10% of the master's defense
export const PET_ENERGY_PER_TURN = 4;          // full pet bar at each pet turn
export const PET_STRIKE_DAMAGE_PERCENT = 30;   // Pet Strike: 30% of a master hit
export const PET_HEAL_PERCENT_MASTER_DEF = 15; // Zone Heal: 15% of master defense EACH
export const PET_BOOST_PERCENT = 10;           // Battle Cry: +10% on the master's next attack
export const PET_BOOST_COOLDOWN = 3;           // pet turns
export const ENEMY_PET_DAMAGE_PERCENT = 30;    // enemy pet AoE: 30% of a master hit

// ── Level architecture (future-proof, unused for now) ────────────────────────
// Pets will level up later (source TBD). All combat numbers should flow through
// petScaling so a level only ever means "a bigger multiplier".
export interface PetProfile {
  id: string;
  name: string;
  img?: string | null;  // equipped familiar artwork (null = emoji fallback)
  level: number;        // 1 for everyone today
}

export const PET_BASE_LEVEL = 1;

/** Global pet power multiplier per level — flat 1.0 until leveling ships. */
export function petScaling(level: number): number {
  void level;
  return 1;
}

/** The only pet stat: max HP, derived from the master (scaled by level). */
export function petMaxHpFor(masterMaxHp: number, masterStats: EquipmentStats, level: number = PET_BASE_LEVEL): number {
  const raw = (PET_HP_PERCENT_MASTER_HP / 100) * masterMaxHp
    + (PET_HP_PERCENT_MASTER_DEF / 100) * masterStats.defense;
  return Math.max(1, Math.round(raw * petScaling(level)));
}

// ── Ally pet spells ───────────────────────────────────────────────────────────

export type PetSpellId = "pet_battle_cry" | "pet_zone_heal" | "pet_strike";

export interface PetSpellDef {
  id: PetSpellId;
  name: string;
  description: string;
  nameFr?: string;
  descFr?: string;
  nameKo?: string;
  descKo?: string;
  icon: string;
  color: string;
  bg: string;
  cost: number;            // pet energy (bar of PET_ENERGY_PER_TURN)
  cooldownTurns?: number;  // pet turns (ticked at pet phase start)
}

export const PET_SPELLS: Record<PetSpellId, PetSpellDef> = {
  pet_battle_cry: {
    id: "pet_battle_cry",
    name: "Battle Cry",
    description: "The master's next attack deals +10% damage. Cooldown: 3 turns.",
    nameFr: "Cri de guerre",
    descFr: "La prochaine attaque du maître inflige +10% de dégâts. Recharge : 3 tours.",
    nameKo: "전투의 함성",
    descKo: "주인의 다음 공격이 +10% 데미지를 입힙니다. 재사용 대기: 3턴.",
    icon: "📣",
    color: "#F5B301",
    bg: "rgba(245,179,1,0.12)",
    cost: 1,
    cooldownTurns: PET_BOOST_COOLDOWN,
  },
  pet_zone_heal: {
    id: "pet_zone_heal",
    name: "Zone Heal",
    description: "Heal the companion AND the master, each for 15% of the master's defense.",
    nameFr: "Soin de zone",
    descFr: "Soigne le familier ET le maître, chacun de 15% de la défense du maître.",
    nameKo: "광역 치유",
    descKo: "동료와 주인을 각각 주인 방어력의 15%만큼 회복합니다.",
    icon: "💞",
    color: "#2DE36B",
    bg: "rgba(45,227,107,0.12)",
    cost: 4,
  },
  pet_strike: {
    id: "pet_strike",
    name: "Pet Strike",
    description: "Deal 30% of the master's attack damage to one enemy.",
    nameFr: "Frappe du familier",
    descFr: "Inflige 30% des dégâts d'attaque du maître à un ennemi.",
    nameKo: "동료의 일격",
    descKo: "적 하나에게 주인 공격 데미지의 30%를 입힙니다.",
    icon: "🐾",
    color: "#4CC9F0",
    bg: "rgba(76,201,240,0.12)",
    cost: 3,
  },
};

export const PET_SPELL_POOL: PetSpellId[] = ["pet_strike", "pet_zone_heal", "pet_battle_cry"];

/** Safe lookup — returns undefined for non-pet ids (mirrors getSpell). */
export function getPetSpell(id: string): PetSpellDef | undefined {
  return (PET_SPELLS as Record<string, PetSpellDef>)[id];
}
